import React from 'react';
import { Box, SxProps, Theme, Typography } from '@mui/material';

/**
 * AuxTrack brand mark.
 *
 * A ring drawn as a clock sweep, opened at the top-right and closed by a
 * dot: time being tracked rather than merely displayed. The inner hand is a
 * single elbow so the whole thing survives being rendered at 16px in a
 * browser tab, which is where most marks fall apart.
 */
export const LogoMark: React.FC<{ size?: number; title?: string }> = ({
  size = 32,
  title = 'AuxTrack',
}) => (
  <Box
    component="svg"
    viewBox="0 0 48 48"
    width={size}
    height={size}
    role="img"
    aria-label={title}
    sx={{ display: 'block', flexShrink: 0 }}
  >
    <defs>
      <linearGradient id="auxtrack-mark" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#6366F1" />
        <stop offset="100%" stopColor="#4338CA" />
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="48" height="48" rx="12" fill="url(#auxtrack-mark)" />
    {/* Clock sweep, open at the top right */}
    <path
      d="M24 11.5a12.5 12.5 0 1 0 12.5 12.5"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="4"
      strokeLinecap="round"
    />
    {/* Terminal dot closing the sweep */}
    <circle cx="36.5" cy="11.5" r="3.6" fill="#A5B4FC" />
    {/* Hands */}
    <path
      d="M24 17.5V24h5.5"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

export const LogoWordmark: React.FC<{ sx?: SxProps<Theme> }> = ({ sx }) => (
  <Typography
    component="span"
    sx={{
      fontFamily: '"Poppins", "Inter", sans-serif',
      fontWeight: 700,
      fontSize: '1.1rem',
      letterSpacing: '-0.02em',
      lineHeight: 1,
      color: 'text.primary',
      whiteSpace: 'nowrap',
      ...sx,
    }}
  >
    Aux
    <Box component="span" sx={{ color: 'primary.main' }}>
      Track
    </Box>
  </Typography>
);

const Logo: React.FC<{ size?: number; showWordmark?: boolean; sx?: SxProps<Theme> }> = ({
  size = 32,
  showWordmark = true,
  sx,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, ...sx }}>
    <LogoMark size={size} />
    {showWordmark && <LogoWordmark />}
  </Box>
);

export default Logo;
